/**
 * Guest-form field model for the NSD events flow.
 * Ported from the Customer Booking prototype (docs/nsd/project/customer/screens.jsx
 * + app/tokens.js `NSD.guestFields`) — the source of truth for the guest page.
 *
 * Deviation from the prototype: year/make/model are marked `required` here,
 * because a Classic booking becomes a real shop appointment and the API rejects
 * it without email plus year, make and model. A VIN or plate is only required
 * when the event asks for one (the fVehicle toggle).
 */

import dayjs from "dayjs";

// Display helpers — API returns raw "2026-10-11" / "08:00"; the design shows
// "Saturday, October 11, 2026" and "8:00 AM – 2:00 PM".
export const formatEventDate = (d) =>
  d ? dayjs(d).format("dddd, MMMM D, YYYY") : "";
const formatTime = (t) => (t ? dayjs(`2000-01-01T${t}`).format("h:mm A") : "");
export const formatWindow = (start, end) =>
  `${formatTime(start)} – ${formatTime(end)}`;

// A VIN is exactly 17 chars, letters + digits, never I/O/Q (ISO 3779).
export const shopTitle = (name) => {
  const n = String(name || "").trim();
  if (!n) return "";
  return /^christian brothers automotive/i.test(n)
    ? n
    : `Christian Brothers Automotive - ${n}`;
};

export const isVin = (s) =>
  /^[A-HJ-NPR-Z0-9]{17}$/.test(String(s || "").toUpperCase());

// Phone and email rules mirror the main scheduler's PersonalInfoScreen checks so
// the two flows accept exactly the same input.
export const isPhone = (s) => {
  const digits = String(s || "").replace(/\D+/g, "");
  return digits.length >= 10 && /^([2-9])(\d{2})(\d{3})(\d{4})$/.test(digits);
};

export const isEmail = (s) =>
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
    String(s || "")
      .toLowerCase()
      .trim()
  );

export const usStates = [
  "AL",
  "AK",
  "AZ",
  "AR",
  "CA",
  "CO",
  "CT",
  "DE",
  "DC",
  "FL",
  "GA",
  "HI",
  "ID",
  "IL",
  "IN",
  "IA",
  "KS",
  "KY",
  "LA",
  "ME",
  "MD",
  "MA",
  "MI",
  "MN",
  "MS",
  "MO",
  "MT",
  "NE",
  "NV",
  "NH",
  "NJ",
  "NM",
  "NY",
  "NC",
  "ND",
  "OH",
  "OK",
  "OR",
  "PA",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VT",
  "VA",
  "WA",
  "WV",
  "WI",
  "WY",
];

/**
 * Ordered list of fields a guest fills in, derived from event type + guestFields
 * toggles. `w` = layout width (half / third / full). An undefined toggle = ON.
 */
export const guestFields = (event = {}, v = {}, opts = {}) => {
  const { years = [], makes = [], models = [] } = opts;
  const classic = event.eventType === "classic" || event.type === "classic";
  const gf = event.guestFields || {};
  const on = (k) => gf[k] !== false;
  const F = [];

  F.push({
    key: "firstName",
    label: "First name",
    required: true,
    ph: "Jane",
    w: "half",
    msg: "Please enter your first name.",
  });
  F.push({
    key: "lastName",
    label: "Last name",
    required: true,
    ph: "Doe",
    w: "half",
    msg: "Please enter your last name.",
  });

  if (classic) {
    F.push({
      key: "phone",
      label: "Phone number",
      required: true,
      ph: "(832) 555-0100",
      w: "half",
      validate: isPhone,
      msg: "Please enter a valid 10-digit phone number.",
    });
    F.push({
      key: "email",
      label: "Email",
      required: true,
      ph: "you@email.com",
      type: "email",
      w: "half",
      validate: isEmail,
      msg: "Please enter a valid email address.",
    });

    if (on("fVehicle")) {
      const idType = v.idType || "plate";
      F.push({
        key: "idType",
        label: "How would you like to identify your vehicle?",
        segmented: true,
        w: "full",
        clears: ["plate", "plateState", "vin"],
        options: [
          { v: "plate", label: "License plate" },
          { v: "vin", label: "VIN" },
        ],
      });
      if (idType === "vin") {
        F.push({
          key: "vin",
          label: "VIN",
          required: true,
          ph: "1HGCM82633A004352",
          w: "full",
          transform: "upper",
          maxLength: 17,
          validate: isVin,
          hint: "The 17-character number on your registration, insurance card, or driver-side dashboard.",
          msg: "Enter a valid 17-character VIN (letters and numbers, no I, O, or Q).",
        });
      } else {
        F.push({
          key: "plateState",
          label: "Plate state",
          required: true,
          select: true,
          options: usStates,
          ph: "State",
          w: "half",
          msg: "Select the state your plate is from.",
        });
        F.push({
          key: "plate",
          label: "License plate",
          required: true,
          ph: "ABC-1234",
          w: "half",
          transform: "upper",
          msg: "Please enter your license plate.",
        });
      }
    }
    // Year/make/model are outside the fVehicle toggle: that toggle only governs the
    // plate/VIN question, and the API rejects a Classic registration without all three.
    F.push({
      key: "year",
      label: "Vehicle year",
      required: true,
      w: "third",
      select: true,
      options: years,
      ph: "Year",
      clears: ["make", "model"],
      msg: "Select a year.",
    });
    F.push({
      key: "make",
      label: "Make",
      required: true,
      w: "third",
      select: true,
      options: makes,
      ph: "Make",
      dependsOn: "year",
      clears: ["model"],
      msg: "Select a make.",
    });
    F.push({
      key: "model",
      label: "Model",
      required: true,
      w: "third",
      select: true,
      ph: "Model",
      dependsOn: "make",
      options: models,
      msg: "Select a model.",
    });
    if (on("fHeard"))
      F.push({
        key: "heard",
        label: "How did you hear about this event?",
        ph: "Church bulletin, friend, social media…",
        w: "full",
      });
    if (on("fVehicleQ"))
      F.push({
        key: "note",
        label: "Any questions about your vehicle? (optional)",
        ph: "e.g. a noise, a warning light…",
        w: "full",
      });
    if (on("fReferral"))
      F.push({
        key: "referral",
        label: "Which church or group invited you? (optional)",
        ph: "Grace Fellowship Church",
        w: "full",
      });
  } else {
    F.push({
      key: "email",
      label: "Email",
      required: true,
      ph: "you@email.com",
      type: "email",
      w: "half",
      validate: isEmail,
      msg: "Please enter a valid email address.",
    });
    F.push({
      key: "phone",
      label: "Phone number",
      ph: "(832) 555-0100",
      w: "half",
      validate: isPhone,
      msg: "Please enter a valid 10-digit phone number.",
    });
    if (on("fHeard"))
      F.push({
        key: "heard",
        label: "How did you hear about this?",
        ph: "Church, friend, social media…",
        w: "full",
      });
    if (on("fReferral"))
      F.push({
        key: "referral",
        label: "Which church or group invited you? (optional)",
        ph: "Grace Fellowship Church",
        w: "full",
      });
  }
  return F;
};
